import { isCourseFree } from "@repo/shared";

import { isRealLesson } from "~/utils/realLessons";

/**
 * La forma de un curso DE CARA AL ALUMNO: cuántos capítulos, cuántas clases, cuántas preguntas
 * rápidas y cuántas evaluaciones. Existe para que la FAQ deje de tener los números escritos a
 * mano: decía "4 capítulos y 7 clases" cuando el curso tenía 10 (el copy quedó del diseño
 * anterior del curso, y antes de ése había dicho 16). Un número copiado en un texto se vuelve
 * mentira la próxima vez que se toca el curso; uno leído del curso, no.
 *
 * Las categorías siguen la regla de `utils/realLessons.ts` (definida por Rodrigo): "clases" son
 * las lecciones de contenido; las de tipo quiz no son clases. Entre los quiz se separan las
 * preguntas rápidas (`isSurvey`: una pregunta sin nota, tipo "¿qué te trajo?") de las
 * evaluaciones con nota.
 */
export type CourseShape = {
  chapters: number;
  lessons: number;
  quickQuestions: number;
  evaluations: number;
};

type ShapeLesson = { type?: string | null; isSurvey?: boolean | null };
type ShapeCourse = { chapters?: ReadonlyArray<{ lessons?: ReadonlyArray<ShapeLesson> | null }> };

export function courseShape(course: ShapeCourse | null | undefined): CourseShape | null {
  const chapters = course?.chapters ?? [];
  if (chapters.length === 0) return null;

  const lessons = chapters.flatMap((chapter) => chapter.lessons ?? []);
  const quizzes = lessons.filter((lesson) => !isRealLesson(lesson));

  return {
    chapters: chapters.length,
    lessons: lessons.filter(isRealLesson).length,
    quickQuestions: quizzes.filter((lesson) => !!lesson.isSurvey).length,
    evaluations: quizzes.filter((lesson) => !lesson.isSurvey).length,
  };
}

type ListedCourse = {
  slug?: string | null;
  isFeatured?: boolean | null;
  priceInCents: number;
  mercadopagoProductId?: string | null;
};

/**
 * El curso del que habla la FAQ ("el curso introductorio"): el destacado si es gratis, y si no
 * el primer gratis. Es el mismo curso que la home pone de protagonista en `FeaturedFreeCourse`,
 * pero sin aceptar un destacado PAGO: la FAQ lo presenta como el curso para empezar.
 */
export function pickIntroCourse<T extends ListedCourse>(courses: ReadonlyArray<T>): T | undefined {
  return (
    courses.find((course) => course.isFeatured && isCourseFree(course)) ??
    courses.find((course) => isCourseFree(course))
  );
}

/**
 * Arma "4 capítulos, 10 clases, 10 preguntas rápidas y una evaluación final" a partir de las
 * piezas ya traducidas. Las piezas en cero no se nombran: si un curso no tiene preguntas rápidas,
 * la frase no las menciona.
 */
export function joinShapeParts(parts: ReadonlyArray<string>): string {
  const present = parts.filter(Boolean);
  if (present.length <= 1) return present[0] ?? "";
  return `${present.slice(0, -1).join(", ")} y ${present[present.length - 1]}`;
}
